
import { Box } from "@mui/material";
import "esri-leaflet-geocoder/dist/esri-leaflet-geocoder.css";
import * as L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import React, { useEffect } from "react";
import { MapContainer, Marker, Polygon, TileLayer, Tooltip, useMap } from "react-leaflet";
import MarkerClusterGroup from 'react-leaflet-cluster';
import LocationListItem from "./Elements/LocationListItem";
import usePrevious from "./Hooks/usePrevious";
import { Coord, Location, Position, Zone } from "./Types/Types";

interface Props {
    current?:Position,
    center?: Position,
    position?: Location,
    locations: Location[],
    zones: Zone[],
    onClickLocation: (e: React.MouseEvent | null, loc : Location) => void,
    bottomOffset?: number,
}

const defaultCoords = {
    lat: 47.436455,
    lng: 12.039587,
};

function Map(props : Props) {
    let debounceTimeout:number;

    const { center, position, locations, zones, onClickLocation, current, bottomOffset } = props;

    const [initialized, setInitialized] = React.useState(false);

    const previous = usePrevious({center, position});
    const RecenterAutomatically = () => {
        const map = useMap();

        const hasPositionChanged = () => {
            if (previous) {
                if (center) {
                    if (initialized === false) {
                        setInitialized(true);
                        return true;
                    }
                    if (previous.center) {
                        if (previous.center.lat === center.lat && previous.center.lng === center.lng) {
                            return false;
                        }
                    }
                }
            }
            return true;
        }

        useEffect(() => {
            if (center) {
                if (hasPositionChanged()) {
                    try {
                        const targetZoom = map.getMaxZoom();

                        var targetPoint = map.project(center, targetZoom).subtract([0, bottomOffset ? -(bottomOffset / 2) : 0]);
                        var targetLatLng = map.unproject(targetPoint, targetZoom);

                        map.setView(targetLatLng, targetZoom);
                    } catch (e) {
                    }
                }
            }
        }, [center]);

        useEffect(() => {
            if (position) {
                debounceTimeout && clearTimeout(debounceTimeout);
                debounceTimeout = window.setTimeout(() => {
                    map.eachLayer((layer : any) => {
                        if (typeof layer.getAllChildMarkers === 'function') {
                            if (layer.getBounds().contains([position.lat, position.lng])) {
                                if (typeof layer.spiderfy === 'function') {
                                    layer.spiderfy();
                                }
                            }
                        }
                    });
                }, 500);
            }
        }, [position]);

        return null;
    }

    const markerIcon = L.icon({
        iconSize: [41, 41],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
        iconUrl: process.env.PUBLIC_URL + '/marker-icon.png',
    });

    const markerIconHighlight = L.icon({
        iconSize: [41, 41],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
        iconUrl: process.env.PUBLIC_URL + '/marker-icon-highlight.png',
    });

    const currentPositionIcon = L.icon({
        iconSize: [41, 41],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
        iconUrl: process.env.PUBLIC_URL + '/green-dot.gif',
    });

    const getLocationMarker = (loc : Location) => {
        if (isLocationSelected(loc)) {
            return markerIconHighlight;
        }
        return markerIcon;
    }

    const isLocationSelected = (loc : Location) => {
        if (position) {
            if (loc.id === position.id) {
                return true;
            }
        }
        return false;
    }

    const Locations = () => {
        return <React.Fragment>
            {locations.map((loc : Location, index : number) => {
                return <Marker
                    icon={getLocationMarker(loc)}
                    key={'loc-' + loc.id}
                    position={[loc.lat, loc.lng]}
                    eventHandlers={{
                        click: (e) => onClickLocation(null, loc),
                    }}
                >
                    {isLocationSelected(loc) && <Tooltip permanent offset={[0, -20]}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationListItem coords={null} location={loc}/>    
                        </Box>
                    </Tooltip>}
                </Marker>;
            })}
        </React.Fragment>;
    }

    const getZones = () => {
        if (!localStorage.getItem('allow-show-zones')) {
            return [];
        }
        return zones;
    }

    return (
        <MapContainer 
            center={[center ? center.lat : defaultCoords.lat, center ? center.lng : defaultCoords.lng]}
            zoom={12}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%', minHeight: '35vh'}}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup
                chunkedLoading={true}
                spiderfyOnMaxZoom={true}
                zoomToBoundsOnClick={true}
                removeOutsideVisibleBounds={false}
            >
                {Locations()}
            </MarkerClusterGroup>
            {current && <Marker
                icon={currentPositionIcon}
                key={'loc-current'}
                position={[current.lat, current.lng]}
                title={"current position"}
            ></Marker>}
            {getZones().map((zone, index) => {
                return <Polygon key={'zone-'+index} positions={zone.polygon[0]?.map((e : Coord) => Object.values(e))}/> 
            })}
            <RecenterAutomatically />
        </MapContainer>
    );
}

export default Map;